"use client";

// Client-side guard for authed pages (C2). This is a stopgap: server-side
// route guarding belongs in src/middleware.ts, which is a CP1 integration
// task outside this lane (flagged in the lane report). Until then an
// unauthenticated visitor gets a client redirect to /login once the
// bootstrap me/refresh ladder settles.
import { useEffect, type ReactNode } from "react";
import { useRouter } from "next/navigation";

import { safeNextTarget } from "@/shared/navigation";

import { useAuth } from "./AuthProvider";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (loading || user) return;
    // Preserve the deep link, the same way src/middleware.ts does on the
    // edge — otherwise the client bounce reintroduces the defect the edge
    // guard just fixed. Read from window rather than useSearchParams():
    // this runs only in an effect so window is always present, and the
    // hook would force every page wrapping this into a Suspense boundary.
    const here = safeNextTarget(`${window.location.pathname}${window.location.search}`);
    router.replace(here ? `/login?next=${encodeURIComponent(here)}` : "/login");
  }, [loading, user, router]);

  // Render nothing while the session settles or the redirect is in flight —
  // authed page content must never flash for a signed-out visitor.
  if (loading || !user) return null;
  return <>{children}</>;
}
